import {
    EzString,
    EzArray
} from '/ezlibrary/helpers/EzHelpers.js';
import { EzEnumeration2 } from '/ezlibrary/enums/EzEnumeration2.js';
import { EzClockerRole } from '/ezlibrary/EzClockerRole.js';

/**
 * @class
 * @extends {EzEnumeration2}
 * @description
 * A template for an ezClocker enumeration class.
 *
 * The following public static methods provided by the EzEnumeration2 parent class:
 *      EzExportAction.ezAsEnum(enumPropertyNameOrValue)
 *      EzExportAction.ezValueOf(enumPropertyName)
 *      EzExportAction.ezNameOf(enumPropertyValue)
 *      EzExportAction.ezEnumData(enumPropertyValue)
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into non-enumeration classes with:
 *      import {
 *          // ... other enumeration classes ...
 *          EzAccountRole
 *      } from '/ezlibrary/enums/EzEnumerations.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into other enumeration classes or classes that cannot import EzEnumerations:
 *      import { EzAccountRole } from '/ezlibrary/enums/EzAccountRole.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Static access to methods, fields, and properties only!
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzAccountRole extends EzEnumeration2 {
    /**
     * @static
     * @public @method
     * Example static method that returns the enumeration property's data.display value.
     * If the enumPropertyValue is not a valid enumeration property value, then EzAccountRole.UNKNOWN.display is returned.
     * @param {undefined|null|string} enumPropertyValue
     * A valid enumeration property value from EzAccountRole
     * @returns {string}
     */
    static ezToDisplayName(enumPropertyValue) {
        return EzString.stringOrEmpty(
            EzString.hasLength(enumPropertyValue)
                ? EzAccountRole.ezEnumData(enumPropertyValue)?.display
                : EzAccountRole.ezEnumData(EzAccountRole.UNKNOWN)?.display);
    }

    /**
     * @static
     * @public @method
     * Transforms the provided EzUserRole enum property value to its equivlant EzAccountRole enum property value.
     * If the provided ezUserRoleEnumValue is not a valid EzUserRole OR does not have a equivlant EzAccountRole enum property value,
     * then EzAccountRole.ROLE_ANONYMOUS is returned.
     * @param {string} [ezUserRoleEnumValue = 'ANONYMOUS']
     * A valid enum property value from EzUserRole
     * @returns {string}
     * A valid enum property value from EzAccountRole
     */
    static fromEzUserRole(ezUserRoleEnumValue = 'ANONYMOUS') {
        if (!EzString.hasText(ezUserRoleEnumValue)) {
            return EzAccountRole.ROLE_ANONYMOUS;
        }

        switch (ezUserRoleEnumValue) {
            case EzAccountRole.ROLE_ADMIN:
                return EzAccountRole.ROLE_ADMIN;
            case EzAccountRole.ROLE_EZCLOCKER_SUPPORT:
                return EzAccountRole.ROLE_EZCLOCKER_SUPPORT;
            case EzAccountRole.ROLE_ENGINEERING_DEBUG:
                return EzAccountRole.ROLE_ENGINEERING_DEBUG;
            case EzAccountRole.ROLE_DEVELOPER:
                return EzAccountRole.ROLE_DEVELOPER;
            case EzAccountRole.ROLE_EMPLOYER:
                return EzAccountRole.ROLE_EMPLOYER;
            case EzAccountRole.ROLE_PAYROLL_MANAGER:
                return EzAccountRole.ROLE_PAYROLL_MANAGER;
            case EzAccountRole.ROLE_MANAGER:
                return EzAccountRole.ROLE_MANAGER;
            case EzAccountRole.ROLE_EMPLOYEE:
                return EzAccountRole.ROLE_EMPLOYEE;
            case EzAccountRole.ROLE_PERSONAL:
                return EzAccountRole.ROLE_PERSONAL;
            case 'UNKNOWN':
            case 'NONE':
            case EzAccountRole.ROLE_ANONYMOUS:
            default:
                return EzAccountRole.ROLE_ANONYMOUS;
        }
    }

    /*
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Initialization Section: ONLY PLACE INITIALIZATION CODE BEYOND THIS SECTION
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */

    /**
     * @private @field
     * Stores the singleton instance of this enumeration class.
     * @type {EzAccountRole}
     * Internal static instance of the EzAccountRole
     */
    static #EzAccountRoleSingleton = null;

    /**
     * @static
     * @private @method
     * Builds the enumeration properties and values for EzClockerAccountType
     */
    static #ezBuildEnumeration() {
        this.#EzAccountRoleSingleton.ezBuildEnumProp(
            'ROLE_ANONYMOUS',
            'ROLE_ANONYMOUS',
            new EzClockerRole(
                // EzAccountRole
                'ROLE_ANONYMOUS',
                // EzUserRole (legacy)
                'ROLE_ANONYMOUS',
                // Authority Role Name
                'ROLE_ANONYMOUS',
                // EzClockerAccountType
                'ANONYMOUS',
                // Role display name
                'Anonymous',
                // Context Name
                'anonymousContext',
                // Access Information
                [
                    'Read access to ezClocker unsecured public website pages.',
                    'Read access to the ezClocker Blog Website.',
                    'No access to Employer Dashboard website page.',
                    'No access to Employer Schedules website page.',
                    'No access to Employer Archive website page.',
                    'No access to Employer Account website page.',
                    'No access to the Employee Dashboard website page.',
                    'No access to the Employee Schedule website page.',
                    'No access to the Personal Dashboard website page',
                    'No access to the Developer Account website page.',
                    'No access to the Administration Portal website page.',
                    'No access to the Employer mobile app.',
                    'No access to the Employee mobile app.',
                    'No access to the Personal mobile app.'
                ],
                // Internal only
                false,
                // Account type
                'anonymous',
                // Enabled permissions
                EzArray.EMPTY,
                // Disabled permissions
                EzArray.EMPTY));

        this.#EzAccountRoleSingleton.ezBuildEnumProp(
            'ROLE_ADMIN',
            'ROLE_ADMIN',
            new EzClockerRole(
                // EzAccountRole
                'ROLE_ADMIN',
                // EzUserRole (legacy)
                'ROLE_ADMIN',
                // Authority Role Name
                'ROLE_ADMIN',
                // EzClockerAccountType
                'ADMIN',
                // Role display name
                'Administrator',
                // Context Name
                'adminContext',
                // Access Information
                [
                    'Read access to ezClocker unsecured public website pages.',
                    'Read access to the ezClocker Blog Website.',
                    'Full access to Employer Dashboard website page.',
                    'Full access to Employer Schedules website page.',
                    'Full access to Employer Archive website page.',
                    'Full access to Employer Account website page.',
                    'Full access to the Employee Dashboard website page.',
                    'Full access to the Employee Schedule website page.',
                    'Full access to the Personal Dashboard website page',
                    'Full access to the Developer Account website page.',
                    'Full access to the Administration Portal website page.',
                    'Full access to the Employer mobile app.',
                    'Full access to the Employee mobile app.',
                    'Full access to the Personal mobile app.'
                ],
                // Internal Only
                true,
                // Legacy Account Type
                'administrator',
                // Enabled permissions
                EzArray.EMPTY,
                // Disabled permissions
                EzArray.EMPTY));

        this.#EzAccountRoleSingleton.ezBuildEnumProp(
            'ROLE_EZCLOCKER_SUPPORT',
            'ROLE_EZCLOCKER_SUPPORT',
            new EzClockerRole(
                // EzAccountRole
                'ROLE_EZCLOCKER_SUPPORT',
                // EzUserRole (legacy)
                'ROLE_EZCLOCKER_SUPPORT',
                // Authority Role Name
                'ROLE_EZCLOCKER_SUPPORT',
                // EzClockerAccountType
                'SUPPORT',
                // Role display name
                'Support',
                // Context Name
                'supportContext',
                // Access Information
                [
                    'Read access to ezClocker unsecured public website pages.',
                    'Read access to the ezClocker Blog Website.',
                    'Full access to Employer Dashboard website page.',
                    'Full access to Employer Schedules website page.',
                    'Full access to Employer Archive website page.',
                    'Restricted access to Employer Account website page.',
                    '   Cannot view account payment information.',
                    '   Cannot edit or modify account information.',
                    '   Cannot subscribe.',
                    '   Cannot change subscription plans.',
                    '   Cannot cancel subscription plans.',
                    'Full access to the Employee Dashboard website page.',
                    'Full access to the Employee Schedule website page.',
                    'Full access to the Personal Dashboard website page',
                    'Full access to the Developer Account website page.',
                    'Restricted access to the Administration Portal website page.',
                    'Full access to the Employer mobile app.',
                    'Full access to the Employee mobile app.',
                    'Full access to the Personal mobile app.'
                ],
                // Internal Only
                true,
                // Legacy Account Type
                'support',
                // Enabled permissions
                EzArray.EMPTY,
                // Disabled permissions
                EzArray.EMPTY));

        this.#EzAccountRoleSingleton.ezBuildEnumProp(
            'ROLE_ENGINEERING_DEBUG',
            'ROLE_ENGINEERING_DEBUG',
            new EzClockerRole(
                // EzAccountRole
                'ROLE_ENGINEERING_DEBUG',
                // EzUserRole (legacy)
                'ROLE_ENGINEERING_DEBUG',
                // Authority Role Name
                'ROLE_ENGINEERING_DEBUG',
                // EzClockerAccountType
                'ENGINEERING',
                // Role display name
                'Engineering Debug',
                // Context Name
                'engineeringDebugContext',
                // Access Information
                [
                    'Read access to ezClocker unsecured public website pages.',
                    'Read access to the ezClocker Blog Website.',
                    'Full access to Employer Dashboard website page.',
                    'Full access to Employer Schedules website page.',
                    'Full access to Employer Archive website page.',
                    'Restricted access to Employer Account website page.',
                    '   Cannot view account payment information.',
                    '   Cannot edit or modify account information.',
                    '   Cannot subscribe.',
                    '   Cannot change subscription plans.',
                    '   Cannot cancel subscription plans.',
                    'Full access to the Employee Dashboard website page.',
                    'Full access to the Employee Schedule website page.',
                    'Full access to the Personal Dashboard website page',
                    'Full access to the Developer Account website page.',
                    'Restricted access to the Administration Portal website page.',
                    'Full access to the Employer mobile app.',
                    'Full access to the Employee mobile app.',
                    'Full access to the Personal mobile app.'
                ],
                // Internal Only
                true,
                // Legacy Account Type
                'support',
                // Enabled permissions
                EzArray.EMPTY,
                // Disabled permissions
                EzArray.EMPTY));

        this.#EzAccountRoleSingleton.ezBuildEnumProp(
            'ROLE_DEVELOPER',
            'ROLE_DEVELOPER',
            new EzClockerRole(
                // EzAccountRole
                'ROLE_DEVELOPER',
                // EzUserRole (legacy)
                'ROLE_DEVELOPER',
                // Authority Role Name
                'ROLE_DEVELOPER',
                // EzClockerAccountType
                'DEVELOPER',
                // Role display name
                'Developer',
                // Context Name
                'developerContext',
                [
                    'Read access to ezClocker unsecured public website pages.',
                    'Read access to the ezClocker Blog Website',
                    'Full access to ezClocker\'s Developer Account website page.',
                    'Restricted access to ezClocker\'s Developer Account API.',
                    '   Access requires approval from ezClocker Developer Team.',
                    '   Access requires a valid developer authentication token.',
                    'Restricted access to ezClocker\'s Employer API.',
                    '   Access requires employer approval of developer account.',
                    '   Access requires a valid ezClocker approved developer account.',
                    '   Access requires authentication of an existing employer.',
                    '   Access requires a valid employer authentication token.',
                    'Restricted access to ezClocker\'s Employee API.',
                    '   Access requires employer approval of developer account.',
                    '   Access requires a valid ezClocker approved developer account.',
                    '   Access requires authentication of an exiting employee.',
                    '   Access requires a valid employee authentication token.',
                    'No access to the ezClocker Administration Portal website.'
                ],
                false,
                'developer',
                // Enabled permissions
                EzArray.EMPTY,
                // Disabled permissions
                EzArray.EMPTY))

        this.#EzAccountRoleSingleton.ezBuildEnumProp(
            'ROLE_EMPLOYER',
            'ROLE_EMPLOYER',
            new EzClockerRole(
                // EzAccountRole
                'ROLE_EMPLOYER',
                // EzUserRole (legacy)
                'ROLE_EMPLOYER',
                // Authority Role Name
                'ROLE_EMPLOYER',
                // EzClockerAccountType
                'EMPLOYER',
                // Role display name
                'Employer',
                // Context Name
                'employerContext',
                [
                    'Read access to ezClocker unsecured public website pages.',
                    'Read access to the ezClocker Blog Website',
                    'Full access to the Employer Dashboard website page.',
                    'Full access to the Employer\'s Schedules website page.',
                    'Full access to the Employer\'s Employee Archive website page.',
                    'Full access to the Employer\'s Account website page.',
                    'Full access to the ezClocker Employer mobile app.',
                    'Full access to the ezClocker Employee mobile app.',
                    'No access to the ezClocker Developer Account website page.',
                    'No access to the ezClocker Administration Portal website',
                ],
                false,
                'employer',
                // Enabled permissions
                EzArray.EMPTY,
                // Disabled permissions
                EzArray.EMPTY));

        this.#EzAccountRoleSingleton.ezBuildEnumProp(
            'ROLE_PAYROLL_MANAGER',
            'ROLE_PAYROLL_MANAGER',
            new EzClockerRole(
                // EzAccountRole
                'ROLE_PAYROLL_MANAGER',
                // EzUserRole (legacy)
                'ROLE_PAYROLL_MANAGER',
                // Authority Role Name
                'ROLE_PAYROLL_MANAGER',
                // EzClockerAccountType
                'PAYROLL_MANAGER',
                // Role display name
                'Payroll Manager',
                // Context Name
                'payrollManagerContext',
                [
                    'Read access to ezClocker unsecured public website pages.',
                    'Read access to the ezClocker Blog Website',
                    'Full access to the Employer Dashboard website page.',
                    'Full access to the Employer\'s Schedules website page.',
                    'Full access to the Employer\'s Employee Archive website page.',
                    'No access to the Employer\'s Account website page.',
                    'Full access to the ezClocker Employer mobile app.',
                    'Full access to the ezClocker Employee mobile app.',
                    'No access to the ezClocker Developer Account website page.',
                    'No access to the ezClocker Administration Portal website',
                ],
                false,
                'payroll_manager',
                // Enabled permissions
                EzArray.EMPTY,
                // Disabled permissions
                EzArray.EMPTY));

        this.#EzAccountRoleSingleton.ezBuildEnumProp(
            'ROLE_MANAGER',
            'ROLE_MANAGER',
            new EzClockerRole(
                // EzAccountRole
                'ROLE_MANAGER',
                // EzUserRole (legacy)
                'ROLE_MANAGER',
                // Authority Role Name
                'ROLE_MANAGER',
                // EzClockerAccountType
                'MANAGER',
                // Role display name
                'Manager',
                // Context Name
                'managerContext',
                [
                    'Read access to ezClocker unsecured public website pages.',
                    'Read access to the ezClocker Blog Website',
                    'Restricted access to the Employer Dashboard website page.',
                    '   Cannot see another employe\'s pay rate.',
                    '   Cannot export time sheets.',
                    'Full access to the Employer\'s Schedules website page.',
                    'Full access to the Employer\'s Employee Archive website page.',
                    'No access to the Employer\'s Account website page.',
                    'Full access to the ezClocker Employer mobile app',
                    'Full access to the ezClocker Employee mobile app (for themselves)',
                    'No access to the ezClocker Developer Account website page.',
                    'No access to the ezClocker Administration Portal website.',
                ],
                false,
                'manager',
                // Enabled permissions
                EzArray.EMPTY,
                // Disabled permissions
                EzArray.EMPTY));

        this.#EzAccountRoleSingleton.ezBuildEnumProp(
            'ROLE_EMPLOYEE',
            'ROLE_EMPLOYEE',
            new EzClockerRole(
                // EzAccountRole
                'ROLE_EMPLOYEE',
                // EzUserRole (legacy)
                'ROLE_EMPLOYEE',
                // Authority Role Name
                'ROLE_EMPLOYEE',
                // EzClockerAccountType
                'EMPLOYEE',
                // Role display name
                'Employee',
                // Context Name
                'employeeContext',
                [
                    'Read access to ezClocker unsecured public website pages.',
                    'Read access to the ezClocker Blog Website',
                    'Full access to the ezClocker Employee Dashboard website page.',
                    'Full access to the ezClocker Employee Schedule website page.',
                    'Full access to the ezClocker Employee mobile app.',
                    'No access to the Employer\'s Dashboard website page.',
                    'No access to the Employer\'s Schedules website page.',
                    'No access to the Employer\'s Archive Employee website page.',
                    'No access to the Employer\'s Account website page.',
                    'No access to the ezClocker Developer Account website page.',
                    'No access to the ezClocker Administration Portal website.'
                ],
                false,
                'employee',
                // Enabled permissions
                EzArray.EMPTY,
                // Disabled permissions
                EzArray.EMPTY));

        this.#EzAccountRoleSingleton.ezBuildEnumProp(
            'ROLE_PERSONAL',
            'ROLE_PERSONAL',
            new EzClockerRole(
                // EzAccountRole
                'ROLE_PERSONAL',
                // EzUserRole (legacy)
                'ROLE_PERSONAL',
                // Authority Role Name
                'ROLE_PERSONAL',
                // EzClockerAccountType
                'PERSONAL',
                // Role display name
                'Personal',
                // Context Name
                'personalContext',
                [
                    'Read access to ezClocker unsecured public website pages.',
                    'Read access to the ezClocker Blog Website',
                    'Full access to the ezClocker Personal Dashboard website page.',
                    'Full access to the ezClocker Personal mobile app.',
                    'No access to the ezClocker Employer Dashboard website page.',
                    'No access to the ezClocker Employer Schedules website page.',
                    'No access to the ezClocker Employee Archive website page.',
                    'No access to the ezClocker Employer Account website page.',
                    'No access to the ezClocker Developer Account website page.',
                    'No access to the ezClocker Administration Portal website.',
                    'No access to the ezClocker Employer mobile app.'
                ],
                false,
                'personal',
                // Enabled permissions
                EzArray.EMPTY,
                // Disabled permissions
                EzArray.EMPTY));
    }

    /**
     * Static Initialization of EzExportAction
     */
    static {
        if (null == this.#EzAccountRoleSingleton) {
            this.#EzAccountRoleSingleton = new EzAccountRole();

            this.#ezBuildEnumeration();

            globalThis.EzAccountRole = this;
        }
    }

    /*
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * End of Initialziation Section: !! DO NOT PLACE ANY CODE BEYOND THIS SECTION !!
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */
}
